// Cost topics — which heading each line of the Job Costs statement reads under.
//
// THE RULE THIS FILE EXISTS TO KEEP: moving a cost between topics REGROUPS
// money, it never CHANGES money. Every function here re-files an amount; not
// one of them can alter it, scale it, drop it or invent it. `rollUpTopics`
// returns `grand` for exactly that reason — it is a sum over the ITEMS, so it
// comes out the same whatever the topics say, and the caller can assert it.
//
// A topic is a HEADING, and only a heading. It never decides how a cost is
// treated underneath: every line carries its share of overhead, whichever
// topic it is filed under, and moving it cannot change that. (Rented scaffold
// used to be the one exception — supplier spend that bypassed direct cost.
// The owner ended that on 2026-07-31: quoting the scaffold, checking the
// invoice and scheduling the delivery are his office's time like anything
// else. There is now no source that behaves differently from its neighbours.)
//
// Pure arithmetic on plain objects: no DOM, no network, no Supabase.
//
// A cost item is the shape the Job Costs tab feeds in — one line of the
// statement, already priced:
//   { source, ref, defaultTopic, amount }
//   source        which feed the line came from (see TOPIC_SOURCES)
//   ref           names the line inside that feed
//   defaultTopic  the topic the app derives when nobody has moved it
//   amount        what it cost, in dollars

/**
 * The six topics every org has. They live HERE, in code, and not in
 * job_cost_topics — which is what makes them impossible to rename, delete or
 * shadow. `budgetColumn` is the job_budgets column that holds their budget;
 * 'other' has none on purpose (it is the catch-all for what fit nowhere, not
 * something you set money aside for) and custom topics use extra_budgets.
 */
export const BUILT_IN_TOPICS = Object.freeze([
  Object.freeze({ key: 'material',  labelKey: 'jc.material',  budgetColumn: 'material_budget',  code: 'MAT' }),
  Object.freeze({ key: 'labour',    labelKey: 'jc.labour',    budgetColumn: 'labour_budget',    code: 'LAB' }),
  Object.freeze({ key: 'equipment', labelKey: 'jc.equipment', budgetColumn: 'equipment_budget', code: 'EQP' }),
  Object.freeze({ key: 'trucking',  labelKey: 'jc.trucking',  budgetColumn: 'trucking_budget',  code: 'TRA' }),
  Object.freeze({ key: 'safety',    labelKey: 'jc.safety',    budgetColumn: 'safety_budget',    code: 'SAF' }),
  Object.freeze({ key: 'other',     labelKey: 'jc.other',     budgetColumn: null,               code: 'OTH' }),
]);

/**
 * The feeds whose lines can be moved with an override row. Mirrors the CHECK
 * on project_cost_topics.source — if the two ever disagree the save fails
 * loudly at the database rather than half-working.
 *
 * 'entry' is absent by design: a typed cost (job_cost_entries) is moved by
 * updating its own `category` column, never by writing an override. One home
 * for that answer, so two cannot disagree.
 *
 * 'recipe' is the material cost a job draws from what its production items
 * consume instead of from deliveries (js/core/cost-recipe.js). Its ref is a
 * material_type_id — a uuid, so unlike labour_trade and scaffold it survives
 * the item being renamed. A job costs by ONE of the two methods, so the
 * statement never carries 'delivery' rows and 'recipe' rows that both count.
 */
export const TOPIC_SOURCES = Object.freeze(
  ['delivery', 'tax', 'labour_trade', 'sub_crew', 'equipment_cost', 'scaffold', 'recipe']);

/** Where a cost goes when the topic it was filed under no longer exists. */
export const FALLBACK_TOPIC = 'other';

/**
 * Every custom key starts with this. That prefix is the whole collision
 * story: no built-in key starts with 'c_', and `isCustomTopicKey` refuses a
 * key that does not, so a custom topic can never be named 'material' and
 * quietly take over a built-in bucket. Enforced again by a CHECK constraint
 * on both new tables, so it holds even against SQL typed by hand.
 */
export const CUSTOM_PREFIX = 'c_';

const CUSTOM_KEY_RE = /^c_[a-z0-9_]{1,48}$/;
const BUILT_IN_KEYS = new Set(BUILT_IN_TOPICS.map(t => t.key));

export function isBuiltInTopic(key) { return BUILT_IN_KEYS.has(key); }
export function isCustomTopicKey(key) {
  return typeof key === 'string' && CUSTOM_KEY_RE.test(key);
}
export function isTopicKey(key) { return isBuiltInTopic(key) || isCustomTopicKey(key); }

/**
 * Turn what the owner typed into a key that cannot collide with a built-in
 * or with a topic he already made. `taken` is the keys already in use.
 *
 * Returns null only for a blank label. A label with no a–z0–9 in it at all
 * (say, one written in Chinese) slugs to nothing, so it becomes 'c_topic' —
 * the KEY is plumbing nobody sees, the LABEL is what shows on screen and is
 * kept exactly as typed.
 */
export function customTopicKey(label, taken) {
  const trimmed = String(label == null ? '' : label).trim();
  if (!trimmed) return null;
  const base = trimmed.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 44) || 'topic';
  const takenSet = taken instanceof Set ? taken : new Set(taken || []);
  let key = CUSTOM_PREFIX + base;
  for (let n = 2; takenSet.has(key); n += 1) key = CUSTOM_PREFIX + base + '_' + n;
  return key;
}

/**
 * The key both the override map and the resolved map are stored under. NUL is
 * the separator because a trade name or a piece of scaffold can contain very
 * nearly anything else — including the '|' and ':' that would otherwise be
 * the obvious choice.
 */
export function itemRef(source, ref) {
  return String(source == null ? '' : source) + '\u0000' + String(ref == null ? '' : ref);
}

/** project_cost_topics rows → { itemRef: topic_key }. */
export function buildOverrideMap(rows) {
  const map = {};
  (rows || []).forEach(r => {
    if (!r || !r.source) return;
    map[itemRef(r.source, r.source_ref)] = r.topic_key;
  });
  return map;
}

/**
 * The topic one line reads under, and the ONLY place that question is
 * answered — the bucket bar and the statement group both call through here,
 * so they cannot show a line in two different places.
 *
 * Degrading is deliberate and silent, in this order: the override, then the
 * line's own default, then 'other'. An override can point at a topic the
 * owner has since deleted, and two of the six feeds are keyed by a NAME (a
 * trade, a piece of scaffold) that can be renamed out from under it. Either
 * way the line falls back and keeps its money — a stale override must never
 * be able to make a cost disappear.
 */
export function resolveItemTopic(item, overrides, validKeys) {
  const valid = validKeys instanceof Set ? validKeys : new Set(validKeys || BUILT_IN_KEYS);
  const wanted = (overrides || {})[itemRef(item && item.source, item && item.ref)];
  if (wanted && valid.has(wanted)) return wanted;
  const def = item && item.defaultTopic;
  if (valid.has(def)) return def;
  return FALLBACK_TOPIC;
}

/**
 * File every cost line under its topic and add up each one.
 *
 * `grand` is the invariant made checkable: a sum over the items, identical no
 * matter how the topics are arranged. If a caller's own money math and `grand`
 * ever drift apart, a feed was added to the statement and forgotten here.
 */
export function rollUpTopics(items, opts) {
  const o = opts || {};
  const overrides = o.overrides || {};
  const valid = o.topicKeys instanceof Set
    ? o.topicKeys : new Set(o.topicKeys || BUILT_IN_KEYS);

  const totals = {}, byTopic = {}, resolved = {};
  const ensure = (k) => {
    if (!(k in totals)) { totals[k] = 0; byTopic[k] = []; }
  };
  valid.forEach(ensure);
  ensure(FALLBACK_TOPIC);   // always exists — it is where an orphan lands

  let grand = 0;
  (items || []).forEach(item => {
    const amount = Number(item && item.amount) || 0;
    const key = resolveItemTopic(item, overrides, valid);
    ensure(key);
    totals[key] += amount;
    byTopic[key].push(item);
    resolved[itemRef(item.source, item.ref)] = key;
    grand += amount;
  });

  return { totals, byTopic, resolved, grand };
}

/**
 * Built-ins first, in their fixed order, then the owner's own by sort_order.
 * `custom` is what tells the render whether the label is a translation key or
 * text the owner typed.
 */
export function topicList(customRows) {
  const built = BUILT_IN_TOPICS.map(t => ({
    key: t.key, labelKey: t.labelKey, label: null,
    budgetColumn: t.budgetColumn, custom: false,
  }));
  const custom = (customRows || [])
    .filter(r => r && isCustomTopicKey(r.key) && r.active !== false)
    .map(r => ({
      key: r.key, labelKey: null, label: String(r.label == null ? '' : r.label),
      budgetColumn: null, custom: true,
      sortOrder: Number(r.sort_order) || 0,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
  return built.concat(custom);
}

const TOPIC_CODE_RE = /^[A-Z0-9]{2,5}$/;

/** A group code (sigla) as printed in grids and chips: 2–5 chars A–Z 0–9. */
export function isTopicCode(code) {
  return typeof code === 'string' && TOPIC_CODE_RE.test(code);
}

/**
 * The code a group gets when nobody typed one: first three alphanumerics of
 * its label, uppercased. 'GRP' when the label has none (same reasoning as
 * customTopicKey: the code must always exist, the label stays as typed).
 * Never produces a code shorter than 2 chars — labels that strip to fewer
 * than 2 alphanumerics fall back to 'GRP'.
 */
export function defaultTopicCode(label) {
  const letters = String(label == null ? '' : label)
    .toUpperCase().replace(/[^A-Z0-9]+/g, '');
  return letters.length < 2 ? 'GRP' : letters.slice(0, 3);
}

/**
 * topicList + how THIS org displays each group. Same rows, same order; every
 * row gains `code`, and a built-in gains `label` when the org renamed it.
 * Renderers keep the one pattern they already use:
 * `t.label ? esc(t.label) : _tJC(t.labelKey)` — an org rename simply makes
 * `label` non-null for a built-in. An invalid stored code is IGNORED (falls
 * back to the default) so a bad row can never print garbage in a money grid.
 */
export function topicMetaList(customRows, overrideRows) {
  const ovByKey = {};
  (overrideRows || []).forEach(r => { if (r && r.topic_key) ovByKey[r.topic_key] = r; });
  const codeByCustomKey = {};
  (customRows || []).forEach(r => { if (r && r.key) codeByCustomKey[r.key] = r.code; });

  return topicList(customRows).map(t => {
    if (t.custom) {
      const stored = codeByCustomKey[t.key];
      return { ...t, code: isTopicCode(stored) ? stored : defaultTopicCode(t.label) };
    }
    const builtIn = BUILT_IN_TOPICS.find(b => b.key === t.key);
    const ov = ovByKey[t.key];
    const ovLabel = ov && String(ov.label == null ? '' : ov.label).trim();
    return {
      ...t,
      label: ovLabel || null,
      code: ov && isTopicCode(ov.code) ? ov.code : builtIn.code,
    };
  });
}

/** The budget set for one topic, or null when it has none / can have none. */
export function topicBudget(budgetRow, topic) {
  if (!budgetRow || !topic) return null;
  if (topic.budgetColumn) {
    const v = Number(budgetRow[topic.budgetColumn]);
    return Number.isFinite(v) ? v : null;
  }
  if (!topic.custom) return null;          // built-in 'other': no budget, by design
  const v = Number((budgetRow.extra_budgets || {})[topic.key]);
  return Number.isFinite(v) ? v : null;
}

/**
 * The budget the ring measures direct cost against.
 *
 * The five built-in columns are summed in exactly the order and grouping the
 * tab has always used, and the custom topics are added afterwards — so a job
 * with no custom topic gets the identical double it got before this existed,
 * down to the last bit. Leaving custom budgets out would be worse than not
 * offering them: a job with $40k budgeted under a topic of the owner's own
 * would read as $40k over.
 */
export function totalBudget(budgetRow, topics) {
  if (!budgetRow) return null;
  let sum = Number(budgetRow.material_budget) + Number(budgetRow.labour_budget)
          + Number(budgetRow.equipment_budget)
          + Number(budgetRow.trucking_budget || 0) + Number(budgetRow.safety_budget || 0);
  (topics || []).forEach(t => {
    if (!t || !t.custom) return;
    const v = Number((budgetRow.extra_budgets || {})[t.key]);
    if (Number.isFinite(v)) sum += v;
  });
  return sum;
}
